import type { ReactNode } from "react";
import { useContext, useEffect } from "react";
import { navigate } from "@reach/router";
import { useTranslation } from "react-i18next";
import GameSettingsContext, {
  type ContextType as GameSettingsContextType
} from "src/components/providers/GameSettingsProvider";
import type { Settings, ExpertLevel } from "src/core/types";
import type { Theme } from "src/theme";
import RadioButton from "../RadioButton";
import RadioButtonGroup from "../RadioButtonGroup";
import LanguageSelector from "../LanguageSelector";
import { logPageView } from "src/hooks/use-analytics";
import { Helmet } from "react-helmet";

type Props = {
  path?: string;
};

const noopUpdate = (d: Settings) => {};
const expertLevels = [
  { labelKey: "settings.difficulty.beginner", value: "beginner" },
  { labelKey: "settings.difficulty.intermediate", value: "intermediate" },
  { labelKey: "settings.difficulty.expert", value: "expert" }
];
const playbackSpeedLevels = [
  { labelKey: "settings.speed.fast", value: 400 },
  { labelKey: "settings.speed.slow", value: 550 },
  { labelKey: "settings.speed.superSlow", value: 750 }
];
const moveHintsOptions = [
  { labelKey: "settings.on", value: "on", id: "hints-on" },
  { labelKey: "settings.off", value: "off", id: "hints-off" }
];

const containerStyle = ({ gridUnit, colors }: Theme) => ({
  position: "fixed" as const,
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  display: "flex",
  flexDirection: "column" as const,
  background: colors.grey95,
  color: colors.textPrimary,
  zIndex: 1000,
  overflow: "hidden"
});

const headerStyle = ({ gridUnit, colors, fontSize }: Theme) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: `${gridUnit * 5}px`,
  borderBottom: `2px solid ${colors.grey8}`,
  background: "white"
});

const titleStyle = ({ gridUnit, fontSize }: Theme) => ({
  fontSize: gridUnit * 6,
  fontWeight: "bold",
  margin: 0
});

const contentStyle = ({ gridUnit }: Theme) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column" as const,
  gap: `${gridUnit * 6}px`,
  padding: `${gridUnit * 5}px`,
  background: "white",
  overflowY: "auto" as const
});

const sectionStyle = ({ gridUnit }: Theme) => ({
  display: "flex",
  flexDirection: "column" as const,
  gap: `${gridUnit * 2}px`
});

const labelStyle = ({ gridUnit, fontSize, colors }: Theme) => ({
  fontSize: fontSize,
  fontWeight: "600",
  color: colors.textPrimary,
  marginBottom: gridUnit
});

const SettingsForm = (props: Props): ReactNode => {
  const { t } = useTranslation();
  const { settings, setGameSettings = noopUpdate, gameMode } = useContext<GameSettingsContextType>(GameSettingsContext);
  const isMultiplayer = gameMode === "multi";

  const onExperLevelChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const expertLevel = event.currentTarget.value as ExpertLevel;
    let updatedSettings = { ...settings, expertLevel };
    setGameSettings(updatedSettings);
  };
  const onPlaybackSpeedChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    let updatedSettings = { ...settings, playbackSpeed: +event.currentTarget.value };
    setGameSettings(updatedSettings);
  };
  const onMoveHintsChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const showHints = event.currentTarget.value === "on";
    setGameSettings({ ...settings, showHints });
  };
  useEffect(() => {
    logPageView("Bao | Game settings", window.location.href);
  });
  return (
    <div css={containerStyle} data-testid="settings-form">
      <Helmet>
        <title>Bawo | Game settings</title>
        <meta name="description" content="Game settings" />
      </Helmet>
      <header css={headerStyle}>
        <h1 css={titleStyle}>{t("settings.title")}</h1>
        <button css={(theme: Theme) => ({ ...theme.primaryButton })} onClick={() => navigate(-1)}>
          {t("common.back")}
        </button>
      </header>
      <div css={contentStyle}>
        <div css={sectionStyle} data-testid="language-setting">
          <LanguageSelector label={t("settings.language")} testId="settings-language-selector" />
        </div>
        {!isMultiplayer && (
          <div css={sectionStyle}>
            <label css={labelStyle} htmlFor="expertLevel">
              {t("settings.aiDifficulty")}
            </label>
            <RadioButtonGroup>
              {expertLevels.map(({ labelKey, value }) => (
                <RadioButton
                  label={t(labelKey)}
                  key={value}
                  id={value}
                  checked={settings.expertLevel === value}
                  value={value}
                  name="expertLevel"
                  onChange={onExperLevelChange}
                />
              ))}
            </RadioButtonGroup>
          </div>
        )}
        <div css={sectionStyle}>
          <label css={labelStyle} htmlFor="playbackSpeed">
            {t("settings.animationSpeed")}
          </label>
          <RadioButtonGroup>
            {playbackSpeedLevels.map(({ labelKey, value }) => (
              <RadioButton
                label={t(labelKey)}
                key={value}
                id={String(value)}
                checked={settings.playbackSpeed === value}
                value={value}
                name="playbackSpeed"
                onChange={onPlaybackSpeedChange}
              />
            ))}
          </RadioButtonGroup>
        </div>
        {!isMultiplayer && (
          <div css={sectionStyle} data-testid="move-hints-setting">
            <span css={labelStyle} id="move-hints-label">
              {t("settings.moveHints")}
            </span>
            <RadioButtonGroup>
              {moveHintsOptions.map(({ labelKey, value, id }) => {
                const hintsOn = settings.showHints !== false;
                const checked = value === "on" ? hintsOn : !hintsOn;
                return (
                  <RadioButton
                    label={t(labelKey)}
                    key={id}
                    id={id}
                    testId={`move-hints-${value}`}
                    checked={checked}
                    value={value}
                    name="moveHints"
                    onChange={onMoveHintsChange}
                  />
                );
              })}
            </RadioButtonGroup>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsForm;
